import React, { useState } from 'react';
import { Gift } from 'lucide-react';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { cn } from '../util/designUtil';
import SendGiftModal from './SendGiftModal';

interface SendGiftCardProps {
  creatorId: string;
  creatorName: string;
  onGiftSent: (credits: number) => void;
}

/**
 * The fan-facing "send a gift" panel on a creator's profile. Its own card,
 * separate from the subscribe panel: a gift is a one-off, not a subscription.
 *
 * The modal is mounted only while it is open, so every open starts from an
 * empty form with no leftover amount or error.
 */
const SendGiftCard: React.FC<SendGiftCardProps> = ({
  creatorId,
  creatorName,
  onGiftSent,
}) => {
  const { theme } = useColorTheme();
  const [open, setOpen] = useState(false);

  return (
    <div className="w-full max-w-full bg-white p-5 sm:p-6 rounded-xl shadow-lg border border-gray-200">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            <Gift className={cn('h-4 w-4 shrink-0', theme.secondary)} />
            Send a gift
          </p>
          <p className="text-xs text-gray-600 break-words">
            A one-off thank-you in credits — this is not a subscription.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            'w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md text-sm font-semibold',
            theme.accent,
          )}
        >
          <Gift className="h-4 w-4" />
          Send a gift
        </button>
      </div>

      {open && (
        <SendGiftModal
          creatorId={creatorId}
          creatorName={creatorName}
          onClose={() => setOpen(false)}
          onSent={onGiftSent}
        />
      )}
    </div>
  );
};

export default SendGiftCard;
